import { translate } from '../../i18n/index.js';
import { useEffect, useMemo, useState } from 'react';
import { Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { referralsApi, type ReferralDashboard } from '../../api/referrals.api.js';

const creditLabels: Record<string, string> = {
  REFEREE_OFFER: translate('pages.customer.referrals.credit_labels.referee_offer'),
  REFERRER_BASE: translate('pages.customer.referrals.credit_labels.referrer_base'),
  MILESTONE_3_FREE_ENTRY: translate(
    'pages.customer.referrals.credit_labels.milestone_3_free_entry',
  ),
  MILESTONE_5_FREE_ENTRIES: translate(
    'pages.customer.referrals.credit_labels.milestone_5_free_entries',
  ),
};

export function Referrals() {
  const [dashboard, setDashboard] = useState<ReferralDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralsApi
      .me()
      .then((res) => setDashboard(res.data))
      .catch(() => setError(translate('pages.customer.referrals.referrals.set_error.referral')))
      .finally(() => setLoading(false));
  }, []);

  const activeCredits = useMemo(
    () =>
      dashboard?.credits.filter(
        (credit) => credit.status === 'ACTIVE' && credit.remainingSessions > 0,
      ) ?? [],
    [dashboard],
  );

  async function copyReferralLink() {
    if (!dashboard) return;
    await navigator.clipboard.writeText(dashboard.referralUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <>
      {loading && (
        <div
          className="af-card"
          style={{
            padding: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: 'var(--af-muted)',
          }}
        >
          <Spinner />
          {translate('pages.customer.referrals.referrals.div_card.loading_referral')}
        </div>
      )}
      {error && <div className="af-error">{error}</div>}

      {dashboard && (
        <div className="af-referral-grid">
          <section className="af-card" style={{ padding: 24 }}>
            <div
              style={{
                color: 'var(--af-muted)',
                fontSize: 12,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 12,
              }}
            >
              {translate('pages.customer.referrals.referrals.div.copy')}
            </div>
            <div
              className="af-mono"
              style={{
                border: '1px solid var(--af-border)',
                borderRadius: 0,
                padding: '14px 16px',
                overflowWrap: 'anywhere',
                background: 'rgba(255,255,255,0.03)',
                marginBottom: 14,
              }}
            >
              {dashboard.referralUrl}
            </div>
            <button
              className="af-primary-btn"
              type="button"
              onClick={() => void copyReferralLink()}
              style={{ width: '100%', padding: 14 }}
            >
              {copied
                ? translate('pages.customer.referrals.referrals.button_primary_btn.label')
                : translate('pages.customer.referrals.referrals.button_primary_btn.label_2')}
            </button>

            <div className="af-referral-milestones">
              {dashboard.milestones.map((milestone) => (
                <div
                  key={milestone.referrals}
                  style={{
                    border: '1px solid var(--af-border)',
                    borderRadius: 0,
                    padding: 14,
                    background: milestone.reached
                      ? 'var(--af-accent-soft)'
                      : 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 900 }}>{milestone.referrals}</div>
                  <div style={{ color: 'var(--af-muted)', fontSize: 12, marginBottom: 8 }}>
                    {translate('pages.customer.referrals.referrals.div.referral')}
                  </div>
                  <Tag
                    color={milestone.reached ? 'var(--af-accent)' : 'var(--af-muted)'}
                    bg={milestone.reached ? 'var(--af-accent-soft)' : 'rgba(255,255,255,0.05)'}
                  >
                    {milestone.reward}
                  </Tag>
                </div>
              ))}
            </div>
          </section>

          <section className="af-card" style={{ padding: 24 }}>
            <div
              style={{
                color: 'var(--af-muted)',
                fontSize: 12,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 12,
              }}
            >
              {translate('pages.customer.referrals.referrals.div.copy_2')}
            </div>
            {activeCredits.length === 0 ? (
              <p style={{ color: 'var(--af-muted)', margin: 0 }}>
                {translate('pages.customer.referrals.referrals.p.description')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activeCredits.map((credit) => (
                  <div
                    key={credit.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 14,
                      borderBottom: '1px solid var(--af-border)',
                      paddingBottom: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800 }}>{creditLabels[credit.source]}</div>
                      <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                        {credit.discountPercent}
                        {translate('pages.customer.referrals.referrals.div.copy_3')}
                      </div>
                    </div>
                    <div style={{ fontWeight: 900 }}>
                      {credit.remainingSessions}
                      {'/'}
                      {credit.totalSessions}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="af-card" style={{ padding: 24, gridColumn: '1 / -1' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                alignItems: 'center',
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  color: 'var(--af-muted)',
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {translate('pages.customer.referrals.referrals.div.referral_2')}
              </div>
              <Tag>
                {dashboard.convertedCount}{' '}
                {translate('pages.customer.referrals.referrals.tag.referral')}
              </Tag>
            </div>
            {dashboard.referrals.length === 0 ? (
              <p style={{ color: 'var(--af-muted)', margin: 0 }}>
                {translate('pages.customer.referrals.referrals.p.description_2')}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dashboard.referrals.map((referral) => (
                  <div
                    key={referral.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 16,
                      alignItems: 'center',
                      borderTop: '1px solid var(--af-border)',
                      paddingTop: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800 }}>
                        {referral.refereeAccount.firstName || referral.refereeAccount.email}
                      </div>
                      <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                        {referral.refereeAccount.email}
                      </div>
                    </div>
                    <Tag
                      color={
                        referral.status === 'CONVERTED' ? 'var(--af-success)' : 'var(--af-muted)'
                      }
                      bg={
                        referral.status === 'CONVERTED'
                          ? 'rgba(74,222,128,0.1)'
                          : 'rgba(255,255,255,0.05)'
                      }
                    >
                      {referral.status === 'CONVERTED'
                        ? translate('pages.customer.referrals.referrals.tag.copy')
                        : translate('pages.customer.referrals.referrals.tag.copy_2')}
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
